import styled from "styled-components";
import type { FC } from "react";
import { FaWindows, FaApple } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { device } from "@src/theme";
import rovrliveLogo from "@assets/img/login-logo.svg";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.margins.xl};
  height: 90vh;
  background-color: ${({ theme }) => theme.colors.darkBg};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.main};
`;

const Title = styled.h1`
  font-size: 32px;
  margin-bottom: ${({ theme }) => theme.margins.large};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.margins.large};
  margin-bottom: ${({ theme }) => theme.margins.large};

  @media ${device.mobile} {
    flex-direction: column;
    align-items: center;
  }
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition:
    background-color 0.3s ease,
    transform 0.2s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${({ theme }) => theme.colors.yellow};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BackButton = styled.button`
  margin-top: ${({ theme }) => theme.margins.medium};
  background: none;
  border: 2px solid ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.white};
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray400};
  }
`;

const Logo = styled.img`
  margin-top: ${({ theme }) => theme.margins["2xl"]};
  width: 160px;
  height: auto;
`;

const WindowsIcon = FaWindows as unknown as FC;
const AppleIcon = FaApple as unknown as FC;

const DownloadDesktopApp = ({
  style,
  goBackText = "Go back to dashboard",
  goBackAction,
}: {
  style?: React.CSSProperties;
  goBackText?: string;
  goBackAction?: () => void;
}) => {
  const navigate = useNavigate();
  const handleGoBack = () => {
    if (goBackAction !== undefined) {
      goBackAction?.();
    } else {
      navigate("/");
    }
  };

  return (
    <Container style={style}>
      <Logo src={rovrliveLogo} alt="ROVR Logo" />
      <Title>Download the Desktop App</Title>
      <ButtonGroup>
        <DownloadButton
          href={process.env.REACT_APP_DOWNLOAD_APP_WINDOWS_URL}
          target="_blank"
        >
          <WindowsIcon /> Windows
        </DownloadButton>
        <DownloadButton
          href={process.env.REACT_APP_DOWNLOAD_APP_MAC_URL}
          target="_blank"
        >
          <AppleIcon /> macOS
        </DownloadButton>
      </ButtonGroup>
      <BackButton onClick={handleGoBack}>{goBackText}</BackButton>
    </Container>
  );
};

export default DownloadDesktopApp;
